import React, { useState } from 'react';
import { Select, MenuItem, Button } from '@mui/material';

import {
  exact,
  calculatePvalueFromZ,
} from "./ExactTest";


const ROISelector = ({ rois, setTableData }) => {
  const [targetROI, setTargetROI] = useState('');
  const [comparisonROI, setComparisonROI] = useState('');

  const handleTargetROIChange = (event) => {
    setTargetROI(event.target.value);
  };

  const handleComparisonROIChange = (event) => {
    setComparisonROI(event.target.value);
  };

  const handleOddsRatioClick = () => {
    // Define the logic for the Odds Ratio calculation here
    const targetRoi = rois.find(r => r.id == targetROI);
    const comparisonRoi = rois.find(r => r.id == comparisonROI);
    // return if no RIO defined
    if (!targetRoi || !comparisonRoi) return;
    // calculate odds
    const tableData = comparisonROI.id == 'l-roi-WholeLane' ?
      calculateOddsRatioIncluded(targetRoi, comparisonRoi) :
      calculateOddsRatioDisjoint(targetRoi, comparisonRoi);
    setTableData(tableData);
    console.log(tableData);
  };

  return (
    <div className="roi-select">
      <Select
        value={targetROI}
        onChange={handleTargetROIChange}
        displayEmpty
        size="small"
        className="roi-dropdown"
      >
        <MenuItem value="" disabled>
          Target ROI
        </MenuItem>
        {rois.map((roi, index) => (
          <MenuItem key={index} value={roi.id}>
            {roi.title}
          </MenuItem>
        ))}
      </Select>
      <Button 
        variant="contained" 
        color="success" 
        size="small" 
        onClick={handleOddsRatioClick}
      >
        Odds Ratio
      </Button>
      <Select
        value={comparisonROI}
        onChange={handleComparisonROIChange}
        displayEmpty
        size="small"
        className="roi-dropdown"
      >
        <MenuItem value="" disabled>
          Comparison ROI
        </MenuItem>
        {rois.map((roi, index) => (
          <MenuItem key={index} value={roi.id}>
            {roi.title}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};

export default ROISelector;


export const object2array = (obj) => {
  return Object.keys(obj).map((g, id) => {
    return { 'id': id, 'gene_name': g, 'count': obj[g] }
  })
}

export const calculateOddsRatioIncluded = (target, comparison) => {
  // A: combined count (all genes, all pixels)
  // G: gene count (selected gene, all pixels)
  // R: ROI count (all genes, in ROI)
  // X: ROI-gene count (selected gene, in ROI)
  // Then the 2x2 contingency table for chi-square test will be:
  // a = X
  // b = R-X
  // c = G-X
  // d = A-G-R+X
  // And the OR  will be
  // OR = ad/bc
  // And the Z-score will be
  // Z = (ad-bc) / sqrt((a+b)(a+c)(c+d)(b+d))  * sqrt(a+b+c+d)

  const A = comparison['totalCount'];
  const R = target['totalCount'];

  const summary = Object.keys(target.countByGene).map((g, id) => {
    const X = target.countByGene[g];
    const G = comparison.countByGene[g];
    const a = X + 0.5;   // Yate's correction
    const b = R - X + 0.5;
    const c = G - X + 0.5;
    const d = A - G - R + X + 0.5;
    const OR = (a * d) / (b * c);
    const Z = (a * d - b * c) / Math.sqrt((a + b) * (a + c) * (c + d) * (b + d)) * Math.sqrt(a + b + c + d);
    const pvalue = calculatePvalueFromZ(Z);
    return {
      'id': id,
      'gene_name': g,
      'targetCount': X,
      'OR': isNaN(OR) ? -1 : OR,
      'pvalue': pvalue,
      'comparisonCount': G,
      'targetFreq': X / R,
      'comparisonFreq': G / A,
      'targetTotal': R,
      'comparisonTotal': A,
    }
  });
  const sorted = summary
    // .filter(g => g['count'] >= cutoff)
    .sort((a, b) => b['OR'] - a['OR']);
  return sorted
}

export const calculateOddsRatioDisjoint = (target, comparison) => {

  // Assuming that they are disjoint, the odds ratio / p-value should be calculated based on the following 2x2 contingency table
  // a = R1G
  // b = R1A-R1G
  // c = R2G
  // d = R2A-R2G
  // OR = (a*d)/(b*c)
  // Z = (a*d-b*c)/sqrt[ (a+b) * (b+c) * (a+c) * (b+d) ]

  const R1A = target['totalCount'];
  const R2A = comparison['totalCount'];

  const summary = Object.keys(target.countByGene).map((g, id) => {
    const R1G = target.countByGene[g];
    const R2G = comparison.countByGene[g];
    const a = R1G + 0.5;  // Yate's correction
    const b = R1A - R1G + 0.5;
    const c = R2G + 0.5;
    const d = R2A - R2G + 0.5;
    const OR = (a * d) / (b * c);
    const Z = (a * d - b * c) / Math.sqrt((a + b) * (a + c) * (c + d) * (b + d)) * Math.sqrt(a + b + c + d);
    let pvalue;
    if (Math.max(a, b, c, d) < 100) {
      pvalue = exact(a, b, c, d);
    } else {
      pvalue = calculatePvalueFromZ(Z);
    }
    return {
      'id': id,
      'gene_name': g,
      'targetCount': R1G,
      'OR': isNaN(OR) ? -1 : OR,   // when target has no matching gene
      'pvalue': pvalue,
      'comparisonCount': R2G,
      'targetFreq': R1G / R1A,
      'comparisonFreq': R2G / R2A,
      'targetTotal': R1A,
      'comparisonTotal': R2A,
    }
  });
  const sorted = summary
    // .filter(g => g['count'] >= cutoff)
    .sort((a, b) => b['OR'] - a['OR']);
  return sorted
}

